import React, {
  isValidElement,
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react";
import type {
  MarketTemplateProps,
  MarketTemplateUpdateMarketType,
} from "../types";
import type { MarketType } from "../components/Market/types";
import { MarketLayout } from "../components/MarketLayout/MarketLayout";
import { MarketTemplateSkeleton } from "../components/MarketTemplateSkeleton/MarketTemplateSkeleton";
import { Nav } from "components/src/components/Nav/Nav";
import { type PillNavItemObjType } from "components/src/components/Nav/types";
import { MarketShowType } from "../components/MarketLayout/types";
import { Card } from "components/src/components/Card/Card";
import { EventHeader } from "./components/EventHeader";

import cx from "components/src/utils/cx";
import isEmpty from "lodash-es/isEmpty";

import { useCssFilter } from "shared/src/hooks/useCssFilter";
import { useAccessibility } from "../js/useMarketTemplateAccessibility";
import { handleShowMoreClick } from "../js/handleShowMoreClick";

const _defaultOnSelectionClick = () => {};
const keysListened = ["Tab", "ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp"];

export const NColGrouped = ({
  id,
  header,
  marketHeader,
  showMarketHeader = true,
  markets = [],
  marketFooter,
  footer = {},
  showBorder = false,
  showBottomBorder = true,
  showVersusDivider = false,
  type = "standard",
  versusText,
  groupBy = null,
  showLimit = "all",
  filters,
  canShowLess = true,
  oddsFormat = "american",
  initiallyOpen = true,
  hasSGP = false,
  hasFirebets,
  hasLivestream,
  isOddsBoost,
  showMoreWagers,
  skeleton,
  sport,
  league,
  competitionType,
  eventDetails,
  showEventHeader = true,
  mode,
  enableInternalSkeleton,
  animation = true,
  showTeamsInEventHeader = false,
  paddingClasses = "cui-pb-sm",
  className = "",
  showEventHeaderChevron = true,
  swapEventHeaderCompetitors,
  eventHeaderCompetitionType,
  scrollType,
  topPadNoHeader = "cui-pt-sm",
  boostLogoType = "full",
  isSelectedBuilder,
  priceDisplayFormatter,
  updateMarket,
  onSelectionClick = _defaultOnSelectionClick,
  onHeaderClick,
  onTemplateClick,
  onEventHeaderClick,
  onFilterClick,
  onShowMoreClick,
  mountHiddenMarkets = true,
  updateMarkets = () => {},
  ...rest
}: MarketTemplateProps & { filters: PillNavItemObjType[] }) => {
  const [finishedRenderSetup, setFinishedRenderSetup] = useState({
    firstTime: true,
    value: false,
  });
  const [ref, setRef] = useState(null);
  const { current: initialShowLimit } = useRef(showLimit);
  const [_showLimit, setShowLimit] = useState(showLimit);
  const [hasBeenToggled, setHasBeenToggled] = useState(false);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [currentMarketButtonIndex, setCurrentMarketButtonIndex] = useState<
    number | null
  >(null);
  const [_activeFilter, setActiveFilter] = useState<string | number | null>(
    "all"
  );

  const _onShowMoreClick = useCallback(() => {
    handleShowMoreClick({
      setShowLimit,
      _showLimit,
      showLimit,
      onShowMoreClick,
    });
  }, [_showLimit, filters, onShowMoreClick]);

  const onActiveItem = useCallback(
    (item) => {
      if (filters && filters.length) {
        const [active] = filters.filter<PillNavItemObjType>(
          (filter): filter is PillNavItemObjType => {
            return "id" in filter && filter?.id === item;
          }
        );
        setActiveFilter(active?.value);
      } else {
        setActiveFilter("all");
      }
    },
    [_activeFilter]
  );

  const _onTemplateClick = useCallback(
    (e) => {
      if (
        typeof onTemplateClick === "function" &&
        e.target?.offsetParent?.nodeName !== "BUTTON" &&
        e.target?.nodeName !== "BUTTON"
        // Restore if they don't want the Competitors to trigger a template click
        // (e.target?.offsetParent?.id.includes("cui__card") || e.target?.offsetParent?.id.includes("cui__market"))
      ) {
        onTemplateClick(e);
      }
    },
    [onTemplateClick]
  );

  const _onToggle = useCallback((payload) => {
    const { isOpen, id } = payload;
    setIsOpen(isOpen);
    if (!hasBeenToggled) setHasBeenToggled(true);
    onHeaderClick && onHeaderClick(payload);
  }, []);

  const handleNavClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // const hasHeader = !!header && typeof header === "object" && !isEmpty(header);
  const hasFilters =
    filters &&
    Array.isArray(filters) &&
    !!filters.length &&
    type !== "rowgrouped";
  const hasEventDetails =
    typeof eventDetails !== "undefined" &&
    !isEmpty(eventDetails) &&
    Boolean(
      eventDetails?.eventName ||
        eventDetails?.isLive ||
        eventDetails?.startTime ||
        eventDetails?.home ||
        eventDetails?.away ||
        eventDetails?.competitor1 ||
        eventDetails?.competitor2
    );
  const _showEventHeader = hasEventDetails && showEventHeader;
  const hideOnFirstCollapsed = !initiallyOpen && !hasBeenToggled;
  const showCardSubheader =
    (_showEventHeader || hasFilters) && !hideOnFirstCollapsed;
  const _hasMarketHeader = showMarketHeader && marketHeader && !hideOnFirstCollapsed;

  const canFilter =
    hasFilters ||
    (canShowLess &&
      initialShowLimit !== "all" &&
      typeof showLimit === "number" &&
      markets?.length >= showLimit);

  const _markets =
    !mountHiddenMarkets &&
    typeof _showLimit === "number" &&
    (_activeFilter === "All" || !hasFilters)
      ? (markets as MarketType[]).slice(0, _showLimit)
      : markets;

  const [filteredElements, unFilteredElements] = useCssFilter(
    ref,
    _activeFilter,
    _showLimit,
    groupBy,
    hasFilters,
    hasBeenToggled,
    type,
    finishedRenderSetup,
    setFinishedRenderSetup,
    !canFilter,
    mountHiddenMarkets,
    scrollType
  );

  const cardClasses = useMemo(()=>{
    return cx(
        {
          "cui-border-solid cui-border-x-0 cui-border-t-0 cui-border-b-1 cui-border-fg-minimal":
            !isOpen && showBottomBorder,
          [paddingClasses]: isOpen && typeof paddingClasses === "string",
          [className]: typeof className === "string" && !!className,
          [topPadNoHeader as string]: typeof topPadNoHeader === "string" && (typeof header !== "object" || (!!header && !header?.hasHeader))
        },
        "cui__market-template"
      )
  },[isOpen, showBottomBorder, paddingClasses, className, topPadNoHeader, header]);
  
  const showMoreTotal = useMemo(() => {
    if (typeof showLimit === "number") {
      if (
        typeof _activeFilter === "string" &&
        _activeFilter?.toLowerCase() === "all"
      ) {
        return markets?.length - showLimit;
      } else {
        return filteredElements?.length - showLimit;
      }
    }
    return markets.length;
  }, [markets?.length, showLimit, _activeFilter, filteredElements]);

  const accessibility = useAccessibility({
    title: header?.title,
    description: header?.description,
    hasSGP,
    isOpen,
  });

  const _canShowLess =
    canShowLess && initialShowLimit !== "all" && showMoreTotal > 0;
  const hasFooterObj =
    footer && typeof footer === "object" && Object.keys(footer).length;
  // Tabbing - keep for now

  if (skeleton)
    return <MarketTemplateSkeleton animation={animation} type={type} />;
  console.log(header, "header")
  return (
    <Card
      preventHeaderClickPropagation={true}
      {...rest}
      id={id as string}
      showBorder={showBorder}
      {...header?.computedHeader}
      collapsible={header?.collapsible}
      className={cardClasses}
      initiallyOpen={initiallyOpen}
      onToggle={_onToggle}
      onCardClick={_onTemplateClick}
      ref={setRef as any}
      {...accessibility}
      animation={animation}
      noPad
    >
      {header && header?.isReactNode ? (
        <Card.Header>{header?.component}</Card.Header>
      ) : null}

      {showCardSubheader ? (
        <Card.SubHeader>
          {_showEventHeader ? (
            <EventHeader
              id={id}
              isOddsBoost={isOddsBoost}
              onEventHeaderClick={onEventHeaderClick}
              eventDetails={eventDetails}
              sport={sport}
              mode={mode}
              showTeamsInEventHeader={showTeamsInEventHeader}
              showChevron={showEventHeaderChevron}
              swapCompetitors={swapEventHeaderCompetitors}
              competitionType={eventHeaderCompetitionType}
              boostLogoType={boostLogoType}
            />
          ) : null}
          {hasFilters ? (
            <Nav
              className="cui-mb-xs"
              size="xs"
              items={filters}
              cssMode={true}
              showControls="none"
              overlay={true}
              onActiveItem={onActiveItem}
              skipLink={null}
              tabbable={false}
              animation={animation}
              variant="pill"
              simple
              forceMaxWidth
              onClick={handleNavClick}
            />
          ) : null}
        </Card.SubHeader>
      ) : null}
      {_hasMarketHeader ? (
        <MarketLayout.Header
          {...{ ...marketHeader, isOddsBoost, noBorder: true, noLabels: type === "rowgrouped" ? true : false}}
          className="cui-mb-1"
        />
      ) : null}
      {!hideOnFirstCollapsed ? (
        <>
          {enableInternalSkeleton &&
          finishedRenderSetup.firstTime &&
          !finishedRenderSetup.value ? (
            <MarketTemplateSkeleton
              showHeader={false}
              showFooter={false}
              animation={animation}
              type={type}
            />
          ) : null}
          <div
            className={cx(
              {
                "cui-opacity-0 cui-absolute":
                  finishedRenderSetup.firstTime &&
                  !finishedRenderSetup.value &&
                  canFilter,
                "cui-opacity-100":
                  (!finishedRenderSetup.firstTime &&
                    finishedRenderSetup.value) ||
                  !canFilter,
              },
              "cui__market-template--markets-wrapper"
            )}
          >
            <MarketLayout.Markets
              markets={_markets}
              marketHeader={marketHeader}
              marketTemplateTitle={header?.title}
              oddsFormat={oddsFormat}
              showVersusDivider={false}
              versusText={versusText}
              showLimit={_showLimit as MarketShowType}
              groupBy={groupBy as string}
              onSelectionClick={onSelectionClick}
              mode={mode}
              sport={sport}
              league={league}
              competitionType={competitionType}
              isOddsBoost={isOddsBoost}
              updateMarket={updateMarket as MarketTemplateUpdateMarketType}
              isSelectedBuilder={isSelectedBuilder}
              priceDisplayFormatter={priceDisplayFormatter}
              type={type === "rowgrouped" ? "rowgrouped" : undefined}
              templateId={id}
            />
          </div>
        </>
      ) : null}

      {isValidElement(footer) && !hideOnFirstCollapsed ? (
        <MarketLayout.Footer
          showLimit={_showLimit as MarketShowType}
          filters={filters}
          onShowMoreClick={_onShowMoreClick}
        >
          {footer}
        </MarketLayout.Footer>
      ) : hasFooterObj ||
        (_showLimit && typeof footer === "object" && !hideOnFirstCollapsed) ? (
        <MarketLayout.Footer
          {...footer}
          showLimit={_showLimit as MarketShowType}
          filters={filters}
          showMore={_showLimit !== "all" && showMoreTotal > 0}
          showMoreTotal={showMoreTotal}
          canShowLess={_canShowLess}
          className="cui-mt-1"
          onShowMoreClick={_onShowMoreClick}
        />
      ) : null}
    </Card>
  );
};
